import { supabase } from "@/integrations/supabase/client";

export interface AccountHistoryEntry {
  account: string;
  description: string;
  useCount: number;
}

const normalizeNif = (nif: string): string =>
  nif.replace(/[\s.\-]/g, "").toUpperCase().replace(/^ES(?=[A-Z0-9]{9}$)/, "");

/**
 * Returns the dominant account for a supplier history list, if any.
 * Criteria: at least `minUses` total uses AND the top account represents
 * more than `dominanceRatio` of all uses (default 80%).
 * The list is expected sorted by useCount desc (as returned by loadAccountHistory).
 */
export const getDominantAccount = (
  list: AccountHistoryEntry[] | undefined,
  options: { minUses?: number; dominanceRatio?: number } = {},
): AccountHistoryEntry | null => {
  if (!list || list.length === 0) return null;
  const minUses = options.minUses ?? 3;
  const dominanceRatio = options.dominanceRatio ?? 0.8;
  const total = list.reduce((s, h) => s + (h.useCount || 0), 0);
  const top = list[0];
  if (!top || (top.useCount || 0) < minUses) return null;
  if (total <= 0) return null;
  if (top.useCount / total < dominanceRatio) return null;
  return top;
};

/**
 * Loads account history for the current user, optionally scoped to a company.
 * Returns a Map keyed by normalized tax_id → list of entries (sorted by useCount desc).
 */
export const loadAccountHistory = async (
  companyId?: string | null,
): Promise<Map<string, AccountHistoryEntry[]>> => {
  const result = new Map<string, AccountHistoryEntry[]>();
  try {
    let q = supabase
      .from("supplier_account_history")
      .select("tax_id, expense_account, description, use_count")
      .order("use_count", { ascending: false });

    if (companyId) q = q.eq("company_id", companyId);

    const { data, error } = await q;
    if (error) {
      console.warn("[accountHistory] load failed:", error.message);
      return result;
    }
    for (const row of data || []) {
      const key = normalizeNif(row.tax_id || "");
      if (!key || !row.expense_account) continue;
      const list = result.get(key) || [];
      list.push({
        account: row.expense_account,
        description: row.description || "",
        useCount: row.use_count ?? 1,
      });
      result.set(key, list);
    }
  } catch (e) {
    console.warn("[accountHistory] unexpected load error:", e);
  }
  return result;
};

interface UsageItem {
  taxId: string;
  expenseAccount: string;
  description: string;
}

/**
 * Records account usage by upserting (taxId, expenseAccount) pairs.
 * Increments use_count for existing rows; inserts new ones.
 * Silent on failure — never blocks the export flow.
 */
export const recordAccountUsage = async (
  items: UsageItem[],
  companyId?: string | null,
): Promise<void> => {
  if (!items.length) return;
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id;
    if (!userId) return;

    // Deduplicate by (taxId, expenseAccount)
    const dedup = new Map<string, UsageItem>();
    for (const it of items) {
      const tax = normalizeNif(it.taxId || "");
      const acc = (it.expenseAccount || "").trim();
      if (!tax || !acc) continue;
      const key = `${tax}::${acc}`;
      if (!dedup.has(key)) dedup.set(key, { taxId: tax, expenseAccount: acc, description: it.description || "" });
    }
    if (dedup.size === 0) return;

    const taxIds = Array.from(new Set(Array.from(dedup.values()).map((d) => d.taxId)));
    let existingQuery = supabase
      .from("supplier_account_history")
      .select("tax_id, expense_account, use_count")
      .eq("user_id", userId)
      .in("tax_id", taxIds);
    if (companyId) existingQuery = existingQuery.eq("company_id", companyId);
    else existingQuery = existingQuery.is("company_id", null);

    const { data: existing, error: readErr } = await existingQuery;
    if (readErr) {
      console.warn("[accountHistory] read existing failed:", readErr.message);
      return;
    }

    const countMap = new Map<string, number>();
    for (const row of existing || []) {
      const key = `${normalizeNif(row.tax_id)}::${(row.expense_account || "").trim()}`;
      countMap.set(key, row.use_count ?? 1);
    }

    const now = new Date().toISOString();
    const rows = Array.from(dedup.entries()).map(([key, item]) => ({
      user_id: userId,
      company_id: companyId ?? null,
      tax_id: item.taxId,
      expense_account: item.expenseAccount,
      description: item.description,
      use_count: (countMap.get(key) ?? 0) + 1,
      last_used_at: now,
    }));

    const { error: upsertErr } = await supabase
      .from("supplier_account_history")
      .upsert(rows, { onConflict: "user_id,company_id,tax_id,expense_account" });
    if (upsertErr) {
      console.warn("[accountHistory] upsert failed:", upsertErr.message);
    }
  } catch (e) {
    console.warn("[accountHistory] unexpected record error:", e);
  }
};
